import {
  Box,
  Flex,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
} from '@chakra-ui/react';
import type { SelectOption } from '@diamond/shared/types';
import { Button, DateFilter, Select } from '@diamond/shared/ui';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import type { KeyboardEvent } from 'react';
import { FormProvider } from 'react-hook-form';

import { CABANG_OPTIONS } from '../../constants';
import type { CollectionFilterBarProps } from '../../types';

const DATE_FILTER_CONFIG = {
  fromField: 'lhc_date-from' as const,
  toField: 'lhc_date-to' as const,
  buttonName: 'Pilih tanggal',
  shortYearFormat: true,
  buttonVariant: 'outline' as const,
};

const SEARCH_CONFIG = {
  placeholder: 'Cari no. LHC',
  buttonText: 'Cari',
};

function FilterSelect({
  id,
  placeholder,
  options,
  value,
  onChange,
}: {
  id: string;
  placeholder: string;
  options: SelectOption[];
  value: SelectOption | null;
  onChange: (newValue: SelectOption | null) => void;
}) {
  return (
    <Box position="relative">
      <Select
        id={id}
        placeholder={placeholder}
        options={options}
        value={value}
        onChange={onChange}
      />
      {value ? (
        <IconButton
          aria-label={`Bersihkan filter ${id}`}
          icon={<Icon as={CloseIcon} boxSize={3.5} />}
          size="xs"
          variant="ghost"
          position="absolute"
          right="30px"
          top="50%"
          transform="translateY(-50%)"
          zIndex={2}
          onMouseDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onClick={() => onChange(null)}
        />
      ) : null}
    </Box>
  );
}

function SearchSection({
  searchValue,
  onSearchChange,
  onClearSearch,
  onSearch,
}: {
  searchValue: string;
  onSearchChange: (value: string) => void;
  onClearSearch: () => void;
  onSearch: () => void;
}) {
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onSearch();
    }
  };

  return (
    <Flex gap={2} alignItems="center">
      <InputGroup minW="160px" maxW={{ md: '527px', lg: '672px' }} w="full">
        <InputLeftElement pointerEvents="none">
          <Icon as={SearchIcon} boxSize={4} color="gray.400" />
        </InputLeftElement>
        <Input
          value={searchValue}
          aria-label="Cari no. LHC"
          placeholder={SEARCH_CONFIG.placeholder}
          bgColor="white"
          onChange={(event) => onSearchChange(event.target.value)}
          onKeyDown={handleKeyDown}
        />
        {searchValue ? (
          <InputRightElement>
            <IconButton
              aria-label="Bersihkan pencarian"
              icon={<Icon as={CloseIcon} boxSize={3.5} />}
              size="xs"
              variant="ghost"
              onClick={onClearSearch}
            />
          </InputRightElement>
        ) : null}
      </InputGroup>
      <Button
        variant="outline"
        borderColor="blue.500"
        color="blue.600"
        _hover={{ bg: 'blue.50' }}
        flexShrink={0}
        onClick={onSearch}
      >
        {SEARCH_CONFIG.buttonText}
      </Button>
    </Flex>
  );
}

export function CollectionFilterBar({
  methods,
  searchValue,
  onSearchChange,
  onClearSearch,
  handleSearch,
  handleDateFilter,
  updateDateParams,
  plantValue,
  onPlantChange,
  plantOptions,
  cabangValue,
  onCabangChange,
  onCreateLHC,
}: CollectionFilterBarProps) {
  return (
    <FormProvider {...methods}>
      <Flex
        w="full"
        gap={3}
        alignItems={{ base: 'stretch', md: 'center' }}
        justifyContent="space-between"
        flexWrap={{ base: 'wrap', xl: 'nowrap' }}
      >
        <Flex
          gap={3}
          alignItems={{ base: 'stretch', md: 'center' }}
          flexWrap={{ base: 'wrap', lg: 'nowrap' }}
        >
          <Button
            leftIcon={<AddIcon fontSize="small" />}
            colorScheme="blue"
            bgColor="blue.500"
            color="white"
            variant="solid"
            onClick={onCreateLHC}
          >
            Buat LHC
          </Button>

          <DateFilter
            fromField={DATE_FILTER_CONFIG.fromField}
            toField={DATE_FILTER_CONFIG.toField}
            shortYearFormat={DATE_FILTER_CONFIG.shortYearFormat}
            buttonVariant={DATE_FILTER_CONFIG.buttonVariant}
            buttonName={DATE_FILTER_CONFIG.buttonName}
            onFilterChange={handleDateFilter}
            onCustomDateChange={updateDateParams}
          />

          <FilterSelect
            id="cabang"
            placeholder="Cabang"
            options={CABANG_OPTIONS}
            value={cabangValue}
            onChange={onCabangChange}
          />

          <FilterSelect
            id="plant"
            placeholder="Plant"
            options={plantOptions}
            value={plantValue}
            onChange={onPlantChange}
          />
        </Flex>

        <SearchSection
          searchValue={searchValue}
          onSearchChange={onSearchChange}
          onClearSearch={onClearSearch}
          onSearch={handleSearch}
        />
      </Flex>
    </FormProvider>
  );
}
